import { Switch, Route } from "wouter";
import { Toaster } from "@/components/ui/toaster";
import { NotificationProvider } from "@/components/ui/notification";
import { cn } from "@/lib/utils";
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/Dashboard";
import Documents from "@/pages/Documents";
import Companies from "@/pages/Companies";
import Reports from "@/pages/Reports";
import TenderPackages from "@/pages/TenderPackages";
import TenderReturns from "@/pages/TenderReturns";
import TenderEventSchedule from "@/pages/TenderEventSchedule";
import TES from "@/pages/TES";
import TendererAuth from "@/pages/TendererAuth";
import TendererMain from "@/pages/TendererMain";
import PackageDetail from "@/pages/PackageDetail";
import PackageView from "@/pages/PackageView";
import Settings from "@/pages/Settings";
import MyHomePage from "@/pages/MyHomePage";
import HelpPage from "@/pages/Help";
import PrivacyPage from "@/pages/Privacy";
import TermsPage from "@/pages/Terms";
import Sidebar from "@/components/layout/Sidebar";
import Header from "@/components/layout/Header";
import { useState, useEffect, useLayoutEffect, createContext, useContext } from "react";
import OAuthLogin from "@/pages/OAuthLogin";
import { useAuth } from "@/hooks/useAuth";
import { LoginPage } from "@/components/auth/LoginPage";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { LoginFlowProvider, useLoginFlow } from "@/contexts/LoginFlowContext";
import { useQuery } from "@tanstack/react-query";
import CONFIG from "@/lib/config";

// Sidebar context
const SidebarContext = createContext<{
  sidebarOpen: boolean;
  toggleSidebar: () => void;
}>({
  sidebarOpen: true,
  toggleSidebar: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

// Admin Console imports
import AdminLogin from "@/pages/AdminLogin";
import AdminRouter from "@/components/admin/AdminRouter";


function App() {


  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };


  // Tracks whether the first authentication check has completed
  const [authFirstCheckDone, setAuthFirstCheckDone] = useState(false);
  
  // Ultra-early Vercel sessionId detection to prevent any login flash
  const isVercelWithSessionId = typeof window !== 'undefined' && 
    window.location.hostname.includes('vercel.app') && 
    new URLSearchParams(window.location.search).has('sessionId');
  
  const { isAuthenticated, isEditor, isLoading, error } = useAuth();

  // One-time extraction of sessionId from URL to prevent re-renders and flash
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const sid = url.searchParams.get('sessionId');
    if (sid) {
      localStorage.setItem('sessionId', sid);
      // 不要在这里清理URL，让useAuth.ts来处理OAuth回调检测
      // url.searchParams.delete('sessionId');
      // window.history.replaceState(null, '', url.toString());
    }
  }, []);

  // Check if we have sessionId in URL or localStorage - define early
  const hasSessionId = typeof window !== 'undefined' && 
    (new URLSearchParams(window.location.search).has('sessionId') || 
     localStorage.getItem('sessionId') !== null);
  
  // Session cookie indicates a server session may already exist (prevents login flash for returning users).
  const hasSessionCookie = typeof window !== 'undefined' && document.cookie.includes('connect.sid=');
  
  // Check if we have sessionId in URL specifically (for initial render detection)
  const hasSessionIdInUrl = typeof window !== 'undefined' && 
    new URLSearchParams(window.location.search).has('sessionId');
  
  // Additional check for Vercel deployment - if we're on Vercel and have a sessionId in URL,
  // we should show loading even if localStorage hasn't been updated yet
  const isVercelDeployment = typeof window !== 'undefined' && 
    window.location.hostname.includes('vercel.app');
  const shouldShowLoadingForVercel = isVercelDeployment && hasSessionIdInUrl;

  // 简化方案：在3-leg authentication出现时，progress bar替代login页面
  // 移除复杂的booting类逻辑，直接通过shouldShowLoading控制
  
  // 最激进的方法：直接注入内联样式隐藏Login页面
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hasSessionIdInUrl = new URLSearchParams(window.location.search).has('sessionId');
      if (hasSessionIdInUrl) {
        // 直接注入内联样式到head
        const style = document.createElement('style');
        style.id = 'login-hide-style';
        style.textContent = `
          .login-page-root,
          [class*="login"],
          [class*="Login"],
          [id*="login"],
          [id*="Login"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            position: absolute !important;
            z-index: -9999 !important;
            pointer-events: none !important;
          }
        `;
        document.head.appendChild(style);
        console.log('🔒 Injected inline style to hide login page');
      } else {
        // 移除样式
        const existingStyle = document.getElementById('login-hide-style');
        if (existingStyle) {
          existingStyle.remove();
        }
      }
    }
  }, []);
  
  // Enhanced Vercel loading logic - show loading for a minimum duration after sessionId is detected
  const [vercelLoadingStartTime, setVercelLoadingStartTime] = useState<number | null>(null);
  const [shouldShowVercelLoading, setShouldShowVercelLoading] = useState(false);
  const [vercelSessionIdDetected, setVercelSessionIdDetected] = useState(false);
  
  useEffect(() => {
    // Immediately detect sessionId in URL on Vercel to prevent login flash
    if (isVercelDeployment && hasSessionIdInUrl && !vercelSessionIdDetected) {
      setVercelSessionIdDetected(true);
      console.log('🚀 Vercel sessionId detected in URL, preventing login flash');
    }
    
    if (isVercelDeployment && hasSessionIdInUrl && !vercelLoadingStartTime) {
      setVercelLoadingStartTime(Date.now());
      setShouldShowVercelLoading(true);
    }
    
    if (vercelLoadingStartTime && isAuthenticated) {
      const elapsed = Date.now() - vercelLoadingStartTime;
      if (elapsed >= 1000) { // Minimum 1 second loading time
        setShouldShowVercelLoading(false);
        setVercelLoadingStartTime(null);
      } else {
        const timer = setTimeout(() => {
          setShouldShowVercelLoading(false);
          setVercelLoadingStartTime(null);
        }, 1000 - elapsed);
        return () => clearTimeout(timer);
      }
    }
  }, [isVercelDeployment, hasSessionIdInUrl, isAuthenticated, vercelLoadingStartTime, vercelSessionIdDetected]);
  
  // 修复OAuth回调检测 - 检测OAuth回调后的重定向
  const isOAuthCallback = typeof window !== 'undefined' && (
    (window.location.pathname.includes('/api/auth/acc/callback') &&
     new URLSearchParams(window.location.search).has('code') &&
     new URLSearchParams(window.location.search).has('state')) ||
    // 检测OAuth回调后的重定向：有sessionId且登录流程已开始且用户已认证
    (hasSessionId && localStorage.getItem('loginFlowStarted') === 'true' && isAuthenticated) ||
    // 检测在myhome页面且有sessionId且用户已认证（OAuth回调完成后的常见状态）
    (window.location.pathname === '/myhome' && hasSessionId && isAuthenticated)
  );
  
  // Debug OAuth callback detection
  if (typeof window !== 'undefined' && hasSessionId) {
    console.log('🔍 OAuth Callback Debug:');
    console.log('  pathname:', window.location.pathname);
    console.log('  hasCode:', new URLSearchParams(window.location.search).has('code'));
    console.log('  hasState:', new URLSearchParams(window.location.search).has('state'));
    console.log('  hasSessionId:', hasSessionId);
    console.log('  loginFlowStarted:', localStorage.getItem('loginFlowStarted'));
    console.log('  isOAuthCallback:', isOAuthCallback);
  }

  useEffect(() => {
    const handler = () => setShowLogin(true);
    window.addEventListener("show-autodesk-login", handler);
    return () => window.removeEventListener("show-autodesk-login", handler);
  }, []);

  // Mark first auth check as complete when we have a clear conclusion
  useEffect(() => {
    if (isAuthenticated) {
      setAuthFirstCheckDone(true);
      return;
    }
    if (!isLoading && !hasSessionId && !isOAuthCallback) {
      setAuthFirstCheckDone(true);
    }
  }, [isAuthenticated, isLoading, hasSessionId, isOAuthCallback]);



  // Clean up invalid sessionId when authentication fails
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // If we have sessionId but authentication failed and we're not loading, clean up the sessionId
    if (hasSessionId && !isAuthenticated && !isLoading && !isOAuthCallback) {
      console.log('🧹 Cleaning up invalid sessionId');
      localStorage.removeItem('sessionId');
    }
  }, [hasSessionId, isAuthenticated, isLoading, isOAuthCallback]);




  
  // Track if we're on home/myhome routes (where projects need to load)
  const isHomePage = typeof window !== 'undefined' && 
    (window.location.pathname === '/' || window.location.pathname === '/myhome');

  // Query projects when on home page and authenticated - DISABLED to let MyHomePage handle it
  // const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
  //   queryKey: ['user-projects'],
  //   queryFn: async () => {
  //     const { apiRequest } = await import('@/lib/queryClient');
  //     return apiRequest('/api/autodesk/projects');
  //   },
  //   enabled: isAuthenticated && isHomePage && !isLoading,
  // });
  
  // Mock variables since we disabled the query
  const isLoadingProjects = false;
  const projectsData = null;

  // Show loading for initial Vercel deployment, OAuth callback, and page refresh
  // Only show the "Authenticating..." loading UI when we have evidence that an auth flow/session is in progress.
  // For first-time visitors (no sessionId, no OAuth callback), we should render the normal login screen immediately.
  const hasAuthEvidence = hasSessionId || hasSessionIdInUrl || isOAuthCallback || hasSessionCookie;
  const shouldShowLoading = shouldShowLoadingForVercel ||
                           shouldShowVercelLoading ||
                           (isVercelDeployment && hasSessionIdInUrl && !isAuthenticated) ||
                           ((isLoading && !authFirstCheckDone) && hasAuthEvidence); // Gate loading behind auth evidence
                           
  console.log('🔍 LOGIN FLOW DEBUG:', {
    loginFlowStarted: typeof window !== 'undefined' ? localStorage.getItem('loginFlowStarted') : 'undefined',
    shouldShowLoading,
    hasSessionId,
    isAuthenticated,
    isOAuthCallback
  });

  // 简化登录页面显示逻辑：
  // 1. 未认证时显示登录页面
  // 2. 有sessionId但未认证时显示登录页面（OAuth回调）
  const shouldShowLogin = !isAuthenticated;

  // 显示loading页面的条件：
  // 1. 认证成功但在首页时项目还在加载时，显示LoginPage的progress bar
  const shouldShowLoadingPage = (isAuthenticated && hasSessionId && isHomePage && isLoadingProjects); // 认证成功但在首页时项目还在加载时，继续显示LoginPage的progress bar

  // Debug logging for all environments (only in verbose mode)
  if (CONFIG.DEBUG_LEVEL === 'verbose') {
    console.log('🔍 App Debug:', {
      hasSessionId,
      isAuthenticated,
      isOAuthCallback,
      isLoading,
      isHomePage,
      isLoadingProjects,
      shouldShowLoading,
      shouldShowLoadingForVercel,
      authFirstCheckDone,
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'undefined',
      search: typeof window !== 'undefined' ? window.location.search : 'undefined'
    });
    
    // Additional detailed logging for shouldShowLoading calculation
    console.log('🔍 shouldShowLoading breakdown:', {
      condition1: hasSessionId && !isAuthenticated,
      condition2: isOAuthCallback && !isAuthenticated,
      condition3: isAuthenticated && isHomePage && isLoadingProjects,
      condition4: hasSessionId && isLoading,
      condition5: shouldShowLoadingForVercel,
      condition6: shouldShowVercelLoading,
      condition7: (!authFirstCheckDone && (hasSessionId || isOAuthCallback)),
      finalResult: shouldShowLoading
    });

    // Debug logging for shouldShowLogin calculation
    console.log('🔍 shouldShowLogin breakdown:', {
      condition1: authFirstCheckDone && !isAuthenticated && !hasSessionId && !hasSessionIdInUrl && !isOAuthCallback && !isLoading && !shouldShowLoading && !isVercelWithSessionId,
      condition2: hasSessionId && !isAuthenticated,
      condition3: isAuthenticated && hasSessionId && (isLoadingProjects || !projectsData),
      condition4: isAuthenticated && hasSessionId && projectsData && isLoading,
      finalResult: shouldShowLogin
    });
  }

  // 移除booting类逻辑，使用简化的shouldShowLoading控制
  
    // 页面加载时清理登录流程标记，确保只有在用户真正点击登录按钮后才设置
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const loginFlowStarted = localStorage.getItem('loginFlowStarted');
      if (loginFlowStarted === 'true' && !isAuthenticated && !hasSessionId && !hasSessionIdInUrl) {
        // 如果页面加载时发现登录流程标记，但没有认证状态或sessionId，说明是残留的标记
        localStorage.removeItem('loginFlowStarted');
        console.log('🔐 CLEANUP: Removed stale loginFlowStarted on page load');
      }
    }
  }, [isAuthenticated, hasSessionId, hasSessionIdInUrl]);

  // OAuth回调完成后清理登录流程标记
  useEffect(() => {
    if (typeof window !== 'undefined' && isAuthenticated && hasSessionId) {
      const loginFlowStarted = localStorage.getItem('loginFlowStarted');
      console.log('🔍 CLEANUP DEBUG:', {
        isAuthenticated,
        hasSessionId,
        loginFlowStarted,
        willCleanup: loginFlowStarted === 'true'
      });
      if (loginFlowStarted === 'true') {
        // 认证成功后，延迟清理登录流程标记，确保progress bar UI有足够时间显示
        setTimeout(() => {
          localStorage.removeItem('loginFlowStarted');
          console.log('🔐 CLEANUP: Removed loginFlowStarted after OAuth callback completion');
        }, 1000);
      }
    }
  }, [isAuthenticated, hasSessionId]);





  return (
    <NotificationProvider>
      <SidebarContext.Provider value={{ sidebarOpen, toggleSidebar }}>
        <Switch>
        {/* Admin Console routes - independent authentication (highest priority) */}
        <Route path="/admin/login" component={AdminLogin} />
        <Route path="/admin/access-requests" component={AdminRouter} />
        <Route path="/admin/hubs" component={AdminRouter} />
        <Route path="/admin/projects/:id?" component={AdminRouter} />
        <Route path="/admin/data-syncs" component={AdminRouter} />
        <Route path="/admin/users" component={AdminRouter} />
        <Route path="/admin/acc-users" component={AdminRouter} />
        <Route path="/admin/analytics" component={AdminRouter} />
        <Route path="/admin/audit-logs" component={AdminRouter} />
        <Route path="/admin/reports" component={AdminRouter} />
        <Route path="/admin/notifications" component={AdminRouter} />
        <Route path="/admin/settings" component={AdminRouter} />
        <Route path="/admin" component={AdminRouter} />
        
        {/* Access Denied route - show access denied page (highest priority, independent of auth) */}
        <Route path="/access-denied" component={() => <AccessDenied />} />
        
        {/* Privacy and Terms pages - no authentication required */}
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/terms" component={TermsPage} />
        
        {/* External package view - no sidebar/header */}
        <Route path="/package/:shareToken" component={PackageView} />
        
        {/* Tenderer routes - no sidebar/header, no authentication required */}
        <Route path="/tenderer" component={TendererAuth} />
        {/* Unified URL structure: /[tab]/:projectId/tenderer/:companyCode */}
        <Route path="/packages/:projectId/tenderer/:companyCode" component={TendererMain} />
        <Route path="/returns/:projectId/tenderer/:companyCode" component={TendererMain} />
        <Route path="/members/:projectId/tenderer/:companyCode" component={TendererMain} />
        {/* Legacy dashboard route for backward compatibility */}
        <Route path="/dashboard/:projectId/tenderer/:companyCode" component={TendererMain} />
        
        {/* Main app routes with authentication */}
        <Route>
          {shouldShowLoading ? (
            // Show loading page during initial auth check
            <div className="login-page-root">
              <LoginPage 
                forceLoading={true}
                loadingMessage="Authenticating..."
                progress={30}
              />
            </div>
          ) : shouldShowLoadingPage ? (
            // Show loading page if authenticated but data is still loading
            <div className="login-page-root">
              <LoginPage 
                forceLoading={true}
                loadingMessage="Loading projects..."
                progress={70}
              />
            </div>
          ) : shouldShowLogin ? (
            // Show login page if not authenticated
            <div className="login-page-root">
              <LoginPage />
            </div>
          ) : (
            // Main app routes for authenticated users
            <Switch>
              
              {/* Login route - always show login page */}
              <Route path="/login" component={() => (
                <div className="login-page-root">
                  <LoginPage />
                </div>
              )} />
              
              {/* Root route - show myhome if authenticated, login if not */}
              <Route path="/" component={() => (
                !isAuthenticated ? (
                  <div className="login-page-root">
                    <LoginPage />
                  </div>
                ) : !isEditor ? (
                  <AccessDenied />
                ) : (
                  <MyHomePage />
                )
              )} />
              
              {/* MyHome page - only for authenticated users */}
              <Route path="/myhome" component={() => (
                !isAuthenticated ? (
                  <div className="login-page-root">
                    <LoginPage />
                  </div>
                ) : !isEditor ? (
                  <AccessDenied />
                ) : (
                  <MyHomePage />
                )
              )} />


               {/* Protected routes - require authentication and editor privileges */}
               <Route>
                 {!isAuthenticated && !(isVercelDeployment && hasSessionIdInUrl) ? (
                   <div className="login-page-root">
                     <LoginPage />
                   </div>
                 ) : !isAuthenticated && (isVercelDeployment && hasSessionIdInUrl) ? (
                   // Show loading instead of login when Vercel has sessionId in URL
                   <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
                     <div className="text-center">
                       <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
                       <p className="mt-4 text-gray-600">Completing authentication...</p>
                     </div>
                   </div>
                 ) : !isEditor ? (
                   <AccessDenied />
                 ) : (
                   <div className="min-h-screen flex flex-col md:flex-row bg-gray-100">
                     <Sidebar open={sidebarOpen} toggleSidebar={toggleSidebar} />
                     <div className={cn(
                       "w-full h-full flex flex-col min-w-0 flex-1 transition-all duration-300",
                       sidebarOpen ? "md:ml-64" : "md:ml-16"
                     )}>
                       <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
                       <Switch>
                         <Route path="/dashboard" component={() => {
                           // Redirect to MyHome page to let user select a project
                           window.location.href = '/myhome';
                           return null;
                         }} />
                         
                         {/* Project-specific routes following Autodesk ACC pattern */}
                         <Route path="/dashboard/projects/:projectId" component={Dashboard} />
                         <Route path="/documents/projects/:projectId" component={Documents} />
                         <Route path="/tender-packages/projects/:projectId" component={TenderPackages} />
                         <Route path="/tender-packages/projects/:projectId/:id" component={PackageDetail} />
                         <Route path="/tender-packages/projects/:projectId/:parentId/:addendumId" component={PackageDetail} />
                         <Route path="/tender-packages/:id" component={PackageDetail} />
                         <Route path="/tender-returns/projects/:projectId" component={TenderReturns} />
                         <Route path="/tes/projects/:projectId" component={TES} />
                         <Route path="/reports/projects/:projectId" component={Reports} />
                         <Route path="/companies/projects/:projectId" component={Companies} />
                         <Route path="/settings/projects/:projectId" component={Settings} />
                         <Route path="/settings/account-admin" component={Settings} />
                         <Route path="/settings" component={Settings} />
                         <Route path="/help" component={HelpPage} />
                         <Route component={NotFound} />
                       </Switch>
                     </div>
                   </div>
                 )}
               </Route>
            </Switch>
          )}
        </Route>
      </Switch>
      <Toaster />
      {showLogin && (
        <div
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.3)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: 32,
              minWidth: 320,
            }}
          >
            <OAuthLogin />
            <button
              style={{ marginTop: 16 }}
              onClick={() => setShowLogin(false)}
            >
              关闭
            </button>
          </div>
        </div>
      )}
      </SidebarContext.Provider>
    </NotificationProvider>
  );
}

export default App;
